/**
 * Purchases.
 *
 * RevenueCat validates the receipt with Apple / Google / Stripe and then calls
 * our webhook, which is the ONLY thing that credits tokens or sets a
 * subscription. The client never grants anything — it triggers the purchase
 * sheet and then re-reads its balance from the server.
 *
 * Three stores, one customer: the RevenueCat app user id is the Supabase user
 * id on every platform, so a plan bought on the web is the same plan in the
 * iOS app and vice versa. Prices are the same in every store.
 *
 *   native (iOS / Android)  → Capacitor plugin → App Store / Play Store
 *   web                     → @revenuecat/purchases-js → Stripe (Web Billing)
 */
import { isNative, platform } from './supabase.js';

const KEYS = {
  ios: import.meta.env.VITE_RC_KEY_IOS,
  android: import.meta.env.VITE_RC_KEY_ANDROID,
  web: import.meta.env.VITE_RC_KEY_WEB,
};

/** The RevenueCat store this build purchases through, in our schema's terms. */
export function currentStore() {
  return isNative() ? (platform() === 'ios' ? 'app_store' : 'play_store') : 'stripe';
}
export const STORE_LABEL = { app_store: 'the App Store', play_store: 'Google Play', stripe: 'our website', promotional: 'a promotional grant' };

let configured = false;
let web = null;          // Purchases instance on web
let customerEmail = null;

function plugin() {
  return window.Capacitor?.Plugins?.Purchases ?? null;
}

export function purchasesAvailable() {
  if (isNative()) return Boolean(plugin()) && Boolean(KEYS[platform()]);
  return Boolean(KEYS.web);
}

/** Bind the store customer to the signed-in user. Safe to call repeatedly. */
export async function configure(appUserId, email = null) {
  customerEmail = email;
  if (!purchasesAvailable() || configured || !appUserId) return;
  if (isNative()) {
    await plugin().configure({ apiKey: KEYS[platform()], appUserID: appUserId });
  } else {
    const { Purchases } = await import('@revenuecat/purchases-js');
    web = Purchases.isConfigured?.() ? Purchases.getSharedInstance() : Purchases.configure({ apiKey: KEYS.web, appUserId });
  }
  configured = true;
}

/** Store offerings, or null when unconfigured. */
export async function offerings() {
  if (!configured) return null;
  return isNative() ? plugin().getOfferings() : web.getOfferings();
}

/**
 * Buy a product by our SKU. Resolves once the store confirms; the balance
 * updates a moment later when the webhook lands, so callers should refresh().
 */
export async function purchase(productId) {
  if (!purchasesAvailable()) {
    throw new Error(isNative() ? 'Purchases are not configured for this build.'
                               : 'Web checkout is not available yet. Plans are available in the iOS and Android apps.');
  }
  if (!configured) throw new Error('Sign in again to purchase.');
  if (isNative()) return plugin().purchaseStoreProduct({ productIdentifier: productId });

  // Web: find the package whose product is our SKU, then open RevenueCat's
  // hosted Stripe checkout. The promise resolves when the customer completes
  // or closes it.
  const all = await web.getOfferings();
  const pkg = Object.values(all.all ?? {})
    .flatMap((o) => o.availablePackages ?? [])
    .find((p) => (p.webBillingProduct ?? p.rcBillingProduct)?.identifier === productId);
  if (!pkg) throw new Error('This offering is not available on the web yet.');
  return web.purchase({ rcPackage: pkg, customerEmail: customerEmail ?? undefined });
}

export async function restore() {
  if (!configured || !isNative()) return null;
  return plugin().restorePurchases();
}
